import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import useToast from '../../components/toast/useToast';
import { useIdentity } from '../../hooks/useIdentity';
import { useTransport } from '../../hooks/useTransport';
import TransportToggle from '../beam/TransportToggle';
import CodeBubble from './CodeBubble';
import CodeEditor from './CodeEditor';
import LanguagePicker, { LANGUAGES } from './LanguagePicker';
import { detectLanguage } from './paste-detect';
import { sendCodeSnippet } from '../chat/firestore';
import './CodeComposer.css';

const PASTE_DETECT_MIN_CHARS = 20;

function extForLang(langId) {
  const entry = LANGUAGES.find((l) => l.id === langId);
  return entry?.ext ?? langId;
}

/**
 * CodeComposer — modal authoring surface for `attachment.kind:'code'` messages
 * (F12.2). Matches artboard 20 in `design/sharing-screens.jsx`.
 *
 * Composition (top → bottom):
 *   - Title row: code icon + "Share a code snippet" + close X (provided by Modal)
 *   - Controls: LanguagePicker chip + filename input
 *   - Editor: CodeEditor textarea
 *   - Live preview: CodeBubble (wide) bound to the same {lang, title, text}
 *   - Footer: TransportToggle + helper copy + primary "Share to {partyCode}" CTA
 *
 * State rules:
 *   - `lang` defaults to 'ts'. Manual user picks are sticky against paste-detect.
 *   - `title` auto-defaults to `snippet.<ext>` and follows `lang` UNTIL the user
 *     types a custom value; then it's frozen.
 *   - `transport` initial value comes from `useTransport()`, but local edits
 *     do NOT write back to the room default (we treat the toggle here as a
 *     per-snippet override).
 *   - Share button is disabled while `text` is empty/whitespace.
 *
 * Submit:
 *   - Routes to `sendCodeSnippet(...)` with the identity-bound senderId/Name.
 *   - Closes the modal and surfaces a "Snippet shared" toast.
 *   - `Cmd/Ctrl+Enter` in the editor triggers the same path.
 */
const CodeComposer = ({ isOpen, onClose }) => {
  const { user, handle, partyCode, avatarSeed } = useIdentity();
  const [roomTransport] = useTransport();
  const { showToast } = useToast();

  const [lang, setLang] = useState('ts');
  const [title, setTitle] = useState('snippet.ts');
  // Tracks whether the user has typed a custom filename — once true, we stop
  // auto-syncing the filename when lang changes.
  const titleDirtyRef = useRef(false);
  // Tracks whether the user has manually changed the picker — once true, we
  // stop auto-switching on paste-detect.
  const langDirtyRef = useRef(false);
  const [text, setText] = useState('');
  const [transport, setTransport] = useState(roomTransport);

  // Reset state on each open. Don't clear between renders while open; the user
  // may dismiss + reopen and expect a clean slate.
  useEffect(() => {
    if (!isOpen) return;
    setLang('ts');
    setTitle('snippet.ts');
    setText('');
    setTransport(roomTransport);
    titleDirtyRef.current = false;
    langDirtyRef.current = false;
  }, [isOpen, roomTransport]);

  // Keep filename in sync with lang while the user hasn't customised it.
  useEffect(() => {
    if (titleDirtyRef.current) return;
    setTitle(`snippet.${extForLang(lang)}`);
  }, [lang]);

  const handleLangChange = useCallback((next) => {
    langDirtyRef.current = true;
    setLang(next);
  }, []);

  const handleTitleChange = useCallback((event) => {
    titleDirtyRef.current = true;
    setTitle(event.target.value);
  }, []);

  const handleTextChange = useCallback((next) => {
    setText(next);
  }, []);

  // Paste-detect: only auto-switch the picker if the user hasn't already
  // overridden it AND the pasted text is long enough to be a real snippet.
  // Bias to NULL — `detectLanguage` returns null when unsure.
  const handlePaste = useCallback((event) => {
    if (langDirtyRef.current) return;
    const pasted = event.clipboardData?.getData?.('text') ?? '';
    if (pasted.length < PASTE_DETECT_MIN_CHARS) return;
    const detected = detectLanguage(pasted);
    if (detected && detected !== lang) {
      setLang(detected);
    }
  }, [lang]);

  const canSend = text.trim().length > 0;

  const handleShare = useCallback(async () => {
    if (!canSend) return;
    try {
      await sendCodeSnippet({
        code: partyCode,
        lang,
        title,
        text,
        senderId: user,
        senderName: handle,
        avatarSeed,
        via: transport,
      });
      showToast('Snippet shared');
      onClose?.();
    } catch {
      // sendCodeSnippet's failures are rare (network) — surface the user-facing
      // signal but keep the modal open so they can retry without retyping.
      showToast("Couldn't share snippet");
    }
  }, [canSend, partyCode, lang, title, text, user, handle, avatarSeed, transport, showToast, onClose]);

  const titleNode = useMemo(
    () => (
      <>
        <Icon name="code" size={20} stroke="#8fdcff" aria-hidden />
        <span>Share a code snippet</span>
      </>
    ),
    [],
  );

  // Preview safely renders below the editor regardless of empty state — the
  // bubble handles 0-line input gracefully via its highlighter path.
  const previewTitle = title || `snippet.${extForLang(lang)}`;
  const helperCopy =
    transport === 'lan'
      ? 'Direct over LAN'
      : 'Persists in room via Firebase';
  const helperIcon = transport === 'lan' ? 'wifi' : 'cloud';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="card"
      size="lg"
      title={titleNode}
      ariaLabel="Share a code snippet"
      className="code-composer"
    >
      <div className="code-composer__controls">
        <LanguagePicker value={lang} onChange={handleLangChange} />
        <label className="code-composer__filename input-shell">
          <span className="sr-only">Filename</span>
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            placeholder={`filename — snippet.${extForLang(lang)}`}
            aria-label="Filename"
          />
        </label>
      </div>

      <div className="code-composer__editor-wrap" onPaste={handlePaste}>
        <CodeEditor
          value={text}
          onChange={handleTextChange}
          onSubmit={handleShare}
          placeholder="Paste or type your snippet — Cmd/Ctrl+Enter to share"
          ariaLabel="Code body"
          autoFocus
        />
      </div>

      <div className="code-composer__preview" data-testid="code-composer-preview">
        <CodeBubble
          lang={lang}
          title={previewTitle}
          text={text}
          via={transport}
          wide
        />
      </div>

      <div className="code-composer__footer">
        <TransportToggle value={transport} onChange={setTransport} />
        <span className="code-composer__helper">
          <Icon
            name={helperIcon}
            size={13}
            stroke={transport === 'lan' ? 'var(--lan, #2dd4bf)' : 'var(--cloud, #f59e0b)'}
            aria-hidden
          />
          {helperCopy}
        </span>
        <button
          type="button"
          className="code-composer__share btn-warning"
          onClick={handleShare}
          disabled={!canSend}
        >
          <Icon name="send" size={16} stroke="#1b1300" aria-hidden />
          Share to {partyCode}
        </button>
      </div>
    </Modal>
  );
};

export default CodeComposer;
